import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import ModalDialog from '../modal-dialog/modal-dialog.component';
import { HeaderSecondary } from '../typography/typography.styles';
import Button from '../button/button.component';
import { useStore } from '../../stores/store';
import CallCourierForm from '../../features/parcel-creation/call-courier-with-sidebar/call-courier-form.component';
import { TimeSlot } from '../../models/pickupWindowsModels';
import { CourierPickupSlot, Shipment } from '../../models/parcelDeliveryMiles';
import { getCurrentTimezoneOffset } from '../../utils/generic/generic.utils';
import { ModalRescheduleContainer, ModalRescheduleTest } from './styles';
import { handlePaymentRedirect } from '../../utils/payments/payments.utils';

interface Props {
  isOpen: boolean;
  shipment: Shipment;
  text?: string;
  onClose: () => void;
  onConfirm?: (pickupSlot: CourierPickupSlot) => void;
}

const RescheduleModal = ({
  shipment,
  isOpen,
  text,
  onClose,
  onConfirm,
}: Props) => {
  const {
    parcelCreationStore: {
      getAllDates,
      storePickupWindows,
      isLoadingApiStorePickupWindow,
      dateInfo,
      isLoadingApiCallCourier,
    },
  } = useStore();

  const [sidebarPickupInfo, setSidebarPickupInfo] = useState<
    CourierPickupSlot | undefined
  >();

  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      const pickupSlots = await getAllDates(shipment.id, false);
      const firstSlot = pickupSlots?.at(0);
      if (firstSlot) {
        setSidebarPickupInfo({
          time_from: firstSlot.time_slots[0].from,
          time_to: firstSlot.time_slots[0].to,
          date: firstSlot.date,
        });
      }
    };

    init();
  }, [getAllDates, shipment.id]);

  const handleDateChange = (date: string) => {
    if (!dateInfo) return;

    setSidebarPickupInfo((prev) => ({ ...prev, date }));
  };

  const handleTimeChange = ({ from, to }: TimeSlot) => {
    setSidebarPickupInfo((prev) => ({
      ...prev,
      time_from: from,
      time_to: to,
    }));
  };

  const handleChooseClick = async () => {
    const timezone = getCurrentTimezoneOffset();
    const pickupSlot = {
      ...sidebarPickupInfo,
      timezone,
    };
    if (!onConfirm) {
      const link = await storePickupWindows(shipment.id, pickupSlot);

      handlePaymentRedirect(link);

      onClose?.();
      return;
    }

    onClose?.();
    onConfirm(pickupSlot);
  };

  const getText = () =>
    text ??
    t('reschedule_modal_text', {
      courierName: shipment.sender?.carrier.delivery_carrier_name,
      trackingNumber: shipment.tracking_number,
    });

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      isMobileFullScreen
      paddingMobile='2.4rem 1.6rem'
      displayButtons={false}>
      <ModalRescheduleContainer>
        <HeaderSecondary $size='big'>
          {t('reschedule_modal_title', {
            courierName: shipment.sender?.carrier.delivery_carrier_name,
          })}
        </HeaderSecondary>
        <ModalRescheduleTest>{getText()}</ModalRescheduleTest>
        <CallCourierForm
          pickupSlots={dateInfo}
          isLoading={isLoadingApiCallCourier}
          selectedPickupInfo={sidebarPickupInfo}
          hideDivider
          onDateChange={handleDateChange}
          onTimeChange={handleTimeChange}
        />
        <Button
          type='submit'
          $mt='2.4rem'
          isLoading={isLoadingApiStorePickupWindow}
          onClick={handleChooseClick}>
          {t('confirm')}
        </Button>
      </ModalRescheduleContainer>
    </ModalDialog>
  );
};

export default observer(RescheduleModal);
